import { breakpoint, theme, useQuoteActions } from "@utilities";
import styled from "styled-components";
import moment from "moment";
import { PATH_QUOTES } from "@routes";
import { useNavigate } from "react-router-dom";

interface PendingRequestPageProps {
    data?:
        | {
              id: number;
              title: string;
              created_at: string;
              no_of_items: number;
              total_rrp: number;
              shared_by: string;
              quote_share_id: number;
              number_of_shares: number;
          }[]
        | undefined;
    onQuoteAction?: () => void;
}

export const PendingRequestPage = ({ data = [], onQuoteAction }: PendingRequestPageProps) => {
    const navigate = useNavigate();
    const { loadingStates, handleAcceptQuote, handleRejectQuote } = useQuoteActions({
        onSuccess: onQuoteAction,
    });

    const handleOpenDetail = (id: number): void => {
        navigate(`${PATH_QUOTES}/${id}?isPending=true`);
    };

    return (
        <Container>
            {data.map((quote) => (
                <QuoteCard key={quote.id}>
                    <SharedByBanner>
                        <span>
                            Shared by: <strong>{quote.shared_by}</strong>
                        </span>
                    </SharedByBanner>
                    <StyledTopSection>
                        <StyledQuoteTitle>{quote.title}</StyledQuoteTitle>
                    </StyledTopSection>
                    <StyledSavedDate>
                        Date: {moment(quote.created_at).format("DD/MM/YYYY")}
                        <StyledVoucherInfo>
                            {quote.no_of_items} {quote.no_of_items === 1 ? "item" : "items"}
                        </StyledVoucherInfo>
                    </StyledSavedDate>
                    <QuoteDivider />
                    <StyledBottomWrapper>
                        <StyledActionsWrapper onClick={() => handleOpenDetail(quote.id)}>
                            <PriceBlockStyled>
                                <div className="price-label-wrapper">
                                    <span className="price-label"> Total RRP</span>$
                                    {quote.total_rrp.toFixed(2)}
                                </div>
                            </PriceBlockStyled>
                            <ChevronButton type="button">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                    <path
                                        d="M9 18L15 12L9 6"
                                        stroke={theme.colors.main}
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </ChevronButton>
                        </StyledActionsWrapper>
                        <Footer>
                            <CancelButton
                                onClick={() => handleRejectQuote(quote.quote_share_id)}
                                disabled={!!loadingStates[quote.quote_share_id]}
                            >
                                {loadingStates[quote.quote_share_id] === "reject" ? (
                                    <LoadingSpinner />
                                ) : (
                                    "Decline"
                                )}
                            </CancelButton>
                            <ConfirmButton
                                onClick={() => handleAcceptQuote(quote.quote_share_id)}
                                disabled={!!loadingStates[quote.quote_share_id]}
                            >
                                {loadingStates[quote.quote_share_id] === "accept" ? (
                                    <LoadingSpinner />
                                ) : (
                                    "Add to Quotes"
                                )}
                            </ConfirmButton>
                        </Footer>
                    </StyledBottomWrapper>
                </QuoteCard>
            ))}
        </Container>
    );
};
const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

const QuoteCard = styled.div`
    background: #ffffff;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const StyledTopSection = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
`;
const StyledQuoteTitle = styled.div`
    font-size: 18px;
    font-weight: 600;
    font-family: "Plus Jakarta Sans", sans-serif;
    line-height: 120%;
    color: #000000;
`;

const SharedByBanner = styled.div`
    display: flex;
    justify-content: start;
    align-items: center;
    padding: 8px 14px;
    background: ${theme.colors.share_background};

    span {
        font-family: "Poppins", sans-serif;
        font-weight: 400;
        font-size: 12px;
        line-height: 120%;
        color: #6e7375;

        strong {
            font-weight: 400;
            color: ${theme.colors.main};
        }
    }
`;
const StyledSavedDate = styled.div`
    padding: 0 20px 16px;
    color: #6e7375;
    font-size: 12px;
    font-family: "Plus Jakarta Sans", sans-serif;
    display: flex;
    align-items: center;
    gap: 8px;
`;

const QuoteDivider = styled.div`
    width: calc(100% - 32px);
    height: 1px;
    background: #e6e9eb;
    margin: 0 16px;
`;
const StyledBottomWrapper = styled.div`
    display: flex;
    padding: 16px 20px;
    gap: 10px;
    flex-direction: column;
    position: relative;
    .content {
        color: #4f4f4f;
        font-size: 14px;
    }
    .bookingReference {
        color: #131214;
        font-size: 18px;
        font-weight: bold;
    }
    .help-popup-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.5);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 5;
    }
    .help-popup-content {
        background: white;
        padding: 2rem;
        border-radius: ${theme.borderRadius.medium};
        max-width: 400px;
        ${breakpoint.breakMobile`
            width: 330px;
        `}
        p {
            text-align: center;
            line-height: 150%;
            font-family: poppins;
        }
        h3 {
            margin-bottom: 1rem;
        }
        button {
            margin-top: 1rem;
            padding: 8px 16px;
        }
    }
`;
const StyledVoucherInfo = styled.div`
    background-color: ${theme.colors.items_background};
    padding: 5px 12px;
    border-radius: 50px;
    font-size: 14px;
    font-family: "Plus Jakarta Sans", sans-serif;
    font-weight: 600;
    color: ${theme.colors.items_color};
`;
const StyledActionsWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const PriceBlockStyled = styled.div`
    font-family: 'Poppins', sans-serif;
    display: flex;
    flex-direction: column;
    gap: 4px;
    .price-label-wrapper {
        display: flex;
        align-items: center;
        justify-content: flex-start
        gap: 8px;
        .price-label {
            font-size: 16px;
            color: #6E7375;
            font-weight: 400;
            line-height: 120%;
            margin-right : 6px;
        }
        .price-discount-badge-wrapper {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 4px;
            .price-discount-badge {
                font-size: 0.75rem;
                padding: 3px 8px;
                background-color:  ${theme.colors.main};
                color: ${theme.colors.button_text};
                border-radius: ${theme.borderRadius.large};
            }
            .loading-spinner {
                width: 16px;
                height: 16px;
                border: 2px solid #F3F3F3;
                border-top: 2px solid  ${theme.colors.highlight};
                border-radius: 50%;
                animation: spin 1s linear infinite;
            }
            @keyframes spin {
                0% {
                    transform: rotate(0deg);
                }
                100% {
                    transform: rotate(360deg);
                }
            }
        }
    }
   .price-value-wrapper {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        gap: 8px;

        .price-discounted-value {
            font-size: 18px;
            font-weight: 500;
            line-height: 120%;
            color: #333333;
        }

        .price-original-value {
            font-size: 1rem;
            font-weight: 400;
            color: ${theme.colors.subtle};
            text-decoration: line-through;
        }
    }
`;

const LoadingSpinner = styled.div`
    width: 20px;
    height: 20px;
    border: 2px solid #f3f3f3;
    border-top: 2px solid ${theme.colors.highlight};
    border-radius: 50%;
    animation: spin 1s linear infinite;

    @keyframes spin {
        0% {
            transform: rotate(0deg);
        }
        100% {
            transform: rotate(360deg);
        }
    }
`;

const ChevronButton = styled.button`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 24px;
    height: 24px;
    border: none;
    background: transparent;
    cursor: pointer;
    padding: 0;
`;

const Footer = styled.div`
    width: 100%;
    display: flex;
    justify-content: space-between;
    background: #fff;
    z-index: 2;
    position: sticky;
    bottom: 0;

    ${breakpoint.breakTablet`
    position: static;
    width: 100%;
  `}
`;

const CancelButton = styled.button`
    flex: 1;
    padding: 12px 0;
    border: 1px solid ${({ theme }) => theme.colors.main};

    background: white;
    color: ${({ theme }) => theme.colors.button_text1};
    font-size: 16px;
    font-weight: 700;
    border-radius: ${({ theme }) => theme.borderRadius.small};
    cursor: pointer;
    margin-right: 8px;
    display: flex;
    justify-content: center;
    align-items: center;

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

const ConfirmButton = styled.button`
    flex: 1;
    padding: 12px 16px;
    border: none;
    border-radius: ${({ theme }) => theme.borderRadius.small};
    background: linear-gradient(
        to right,
        ${theme.gradients.button[0]},
        ${theme.gradients.button[1]}
    );
    color: ${theme.colors.button_text};
    font-size: 16px;
    font-weight: 700;
    cursor: pointer;
    margin-left: 8px;
    display: flex;
    justify-content: center;
    align-items: center;

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;
